import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { TbRestore } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { Card } from '../../../../../components/card/card';
import { Checkbox } from '../../../../../components/checkbox/checkbox';
import { IconButton } from '../../../../../components/icon-button/icon-button';
import { HasAccess } from '../../../../../features/has-access/has-access';
import { Table } from '../../../../../features/table/table';
import { TableFilterDateTimeRange } from '../../../../../features/table/table-filter/table-filter-date-time-range/table-filter-date-time-range';
import { TableFilterInput } from '../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { formatRuDateTimeString } from '../../../../../helpers/date-time';
import { buildParams, concatPath } from '../../../../../helpers/path';
import { useLang } from '../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../hooks/use-query-params';
import { ManagerPath, PromotionPath, RootPath } from '../../../../../routes/manager/manager-enums';
import { promoStatusMap, PromoStatusType } from '../../../../../store/api/promotion-api';
import { useManagerStore } from '../../../../../store/use-manager-store';
import { usePromotionStore } from '../../../../../store/use-promotion-store';
import s from './promo-table.module.css';
import { UpdatePromoModal } from './update-promo-modal/update-promo-modal';

export const PromoTable = () => {
  const { t, getLocale } = useLang();
  const { addQueryParams } = useQueryParams();
  const { list, handleUpdatePromo, handleResetPromo, putPromo, isLoading } = usePromotionStore();
  const { hasAccess } = useManagerStore();
  const isNotAllowed = !hasAccess('promotion', ['write']);

  const updatePromoModal = useModalParams('updatePromo');

  const handleChangeStatus = async (id: number) => {
    const updatable = list?.find((item) => item.id === id);
    const isFinished = updatable?.status === 'Finished';

    if (isFinished && updatable) {
      updatePromoModal.handleOpenModal({
        promoId: id,
        promoName: getLocale(updatable.name),
      });
      return;
    }
    try {
      const newStatus = updatable?.status === 'Active' ? 'Stopped' : 'Active';
      if (updatable) {
        handleUpdatePromo({ ...updatable, status: newStatus });
      }
      await putPromo();
      handleResetPromo();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Card>
      {updatePromoModal.isOpen && (
        <UpdatePromoModal
          onClose={() => updatePromoModal.handleCloseModal(['promoId', 'promoName'])}
        />
      )}
      <Table
        id="promo-list"
        columnSettings
        frontPagination
        withArchive
        className={s.table}
        onEmpty={{
          title: t('Список акций пуст'),
          text: t('Создайте первую акцию с помощью кнопки выше'),
        }}
        cols={{
          name: {
            label: t('Название'),
            filter: (
              <TableFilterInput
                name="name"
                suggestOptions={list?.map((item) => getLocale(item.name))}
              />
            ),
          },
          description: {
            label: t('Описание'),
            maxWidth: 200,
            filter: (
              <TableFilterInput
                name="description"
                suggestOptions={list?.map((item) => getLocale(item.description))}
              />
            ),
          },
          status: {
            label: t('Статус'),
            filter: (
              <TableFilterMultiSelect
                name="status"
                options={Object.keys(promoStatusMap).map((key) => ({
                  id: key,
                  value: t(promoStatusMap[key as PromoStatusType]),
                }))}
              />
            ),
          },
          activeFrom: {
            label: t('Дата начала'),
            filter: <TableFilterDateTimeRange name="active_from" />,
          },
          activeTo: {
            label: t('Дата завершения'),
            filter: <TableFilterDateTimeRange name="active_to" />,
          },
          actions: { label: t('Действия') },
        }}
        rows={list?.map((item) => {
          return {
            name: getLocale(item.name),
            description: getLocale(item.description),
            status: (
              <Checkbox
                checked={item.status === 'Active'}
                disabled={isLoading || item.status === 'Planned' || item.is_deleted || isNotAllowed}
                onClick={() => handleChangeStatus(item.id)}
                label={t(promoStatusMap[item.status as PromoStatusType])}
              />
            ),
            activeFrom: formatRuDateTimeString(item.active_from),
            activeTo: formatRuDateTimeString(item.active_to),
            actions: (
              <HasAccess service="promotion" permissions={['write']}>
                {item.is_deleted ? (
                  <IconButton
                    tooltip={t('Восстановить')}
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      addQueryParams({
                        restoreId: String(item.id),
                        restoreName: getLocale(item.name),
                      })
                    }
                  >
                    <TbRestore />
                  </IconButton>
                ) : (
                  <div className={s.actions}>
                    <Link
                      to={
                        concatPath([RootPath.Manager, ManagerPath.Promotion, PromotionPath.Edit]) +
                        buildParams({ id: item.id })
                      }
                    >
                      <IconButton tooltip={t('Редактировать')} size="sm" variant="secondary">
                        <AiOutlineEdit />
                      </IconButton>
                    </Link>
                    <IconButton
                      tooltip={t('Удалить')}
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        addQueryParams({
                          deleteId: item.id.toString(),
                          deleteName: getLocale(item.name),
                        })
                      }
                    >
                      <AiOutlineDelete />
                    </IconButton>
                  </div>
                )}
              </HasAccess>
            ),
          };
        })}
      />
    </Card>
  );
};
